/* ==========================================================================
   Wattec — reveal cart prices once Easify has worked them out

   A cart line holding a build with paid Easify upgrades is rendered at the
   PC's base price, and Easify rewrites its price and the subtotal in the
   browser a moment later (about half a second in the drawer, two seconds on
   the cart page). snippets/wattec-easify-pending.liquid marks those lines
   and the subtotal "price pending", which hides the figures behind a
   shimmer; this script takes the mark off as soon as Easify has done its
   work, so the first price the customer sees is the right one.

   Easify signals that it has re-priced a line by adding the class
   "tpo_cart_item_price" to the line's price. When it does:
     - the price under the product name, which Easify never updates, is set
       to the same per-unit figure, so the line never shows two prices;
     - once every pending line is done, the subtotal is revealed as well
       (a moment later, as Easify writes it straight after the lines).

   Whatever happens, nothing stays hidden for more than MAX_WAIT: if Easify
   is slow or absent, the right price is worked out here from the upgrade
   prices stored on the line (safety net 1, below) rather than showing the
   PC's base price. And any line with priced upgrades that somehow reached
   the cart without Easify's tag is tagged, so checkout always charges the
   upgrades (safety net 2).
   ========================================================================== */
(function () {
  if (window.__wattecCartPricing) return;
  window.__wattecCartPricing = true;

  var MAX_WAIT = 4000;
  var SETTLE = 150;
  var seen = new WeakMap();

  function amountFrom(text) {
    var m = String(text || '').replace(/,/g, '').match(/(\d+(?:\.\d+)?)/);
    return m ? parseFloat(m[1]) : null;
  }

  // Writes an amount in the same style as an existing price (its currency
  // sign and anything around the number are kept).
  function formatLike(sample, amount) {
    var figure = amount.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    var text = String(sample || '').trim();
    return /[\d.,]+/.test(text) ? text.replace(/[\d][\d.,]*/, figure) : figure;
  }

  /* ------------------------------------------------------------------
     Safety net 1 — the price shown.
     Easify normally re-prices a line within a second or two. If it hasn't
     by MAX_WAIT (a slow first load of its data, a slow connection), the
     theme's own figure would be the PC's base price without the upgrades.
     Instead, the right price is worked out here from the cart itself: every
     upgrade's price is written into its option value ("48 GB DDR5
     [ +£349.00 ]"), so line = base line price + quantity x upgrades. If
     Easify catches up later it simply writes the same figures over these.
     ------------------------------------------------------------------ */
  var UPGRADE_PRICE = /[\[(]\s*([+-])\s*£?\s*([\d,]+(?:\.\d+)?)\s*[\])]/;

  // Upgrades on one unit of a cart line, in pence.
  function upgradesPerUnit(item) {
    var pence = 0;
    var props = item.properties || {};
    Object.keys(props).forEach(function (key) {
      if (key.charAt(0) === '_') return;
      var m = String(props[key] == null ? '' : props[key]).match(UPGRADE_PRICE);
      if (m) pence += (m[1] === '-' ? -1 : 1) * Math.round(parseFloat(m[2].replace(/,/g, '')) * 100);
    });
    return pence;
  }

  // Easify's own hidden "Custom Option" lines (added at checkout) are never
  // touched by either safety net.
  function isEasifyHelperLine(item) {
    return /^custom option/i.test(item.product_title || item.title || '');
  }

  function money(pence) {
    return '£' + (pence / 100).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // One cart read shared by everything that needs it within a moment.
  var cartPromise = null;
  function readCart() {
    if (!cartPromise) {
      cartPromise = fetch('/cart.js', { cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .catch(function () { return null; });
      setTimeout(function () { cartPromise = null; }, 1500);
    }
    return cartPromise;
  }

  // Which cart line a row on the page stands for ("CartDrawer-Item-2",
  // "CartItem-2" -> the second line).
  function itemForRow(cart, row) {
    var m = (row.id || '').match(/(\d+)$/);
    return m && cart && cart.items ? cart.items[parseInt(m[1], 10) - 1] : null;
  }

  function writeLinePrice(row, item) {
    var add = upgradesPerUnit(item);
    if (!add) return;
    var lineTotal = item.final_line_price + add * item.quantity;
    var prices = row.querySelectorAll('.cart-item__price-wrapper .price');
    var current = prices[prices.length - 1];
    if (current) current.textContent = money(lineTotal);
    var unit = row.querySelector('.wt-unit-price');
    if (unit) unit.textContent = formatLike(unit.textContent, lineTotal / item.quantity / 100);
    row.setAttribute('data-wt-priced', 'theme');
  }

  function writeSubtotal(totals, cart) {
    var extra = 0;
    cart.items.forEach(function (item) {
      if (!isEasifyHelperLine(item)) extra += upgradesPerUnit(item) * item.quantity;
    });
    var value = totals.querySelector('.totals__subtotal-value');
    if (value && extra) value.textContent = money(cart.total_price + extra);
  }

  function settleLine(line, force) {
    var stamped = line.querySelector('.tpo_cart_item_price');
    if (!stamped && !force) return false;
    if (stamped) {
      var unit = line.querySelector('.wt-unit-price');
      var total = amountFrom(stamped.textContent);
      var qty = parseInt(line.getAttribute('data-wt-qty'), 10) || 1;
      if (unit && total) unit.textContent = formatLike(unit.textContent, total / qty);
      line.classList.remove('wt-price-pending');
      return true;
    }
    // Timed out without Easify: show the worked-out price, then reveal.
    if (line.__wtFallback) return true;
    line.__wtFallback = true;
    readCart().then(function (cart) {
      var item = itemForRow(cart, line);
      if (item && !line.querySelector('.tpo_cart_item_price')) writeLinePrice(line, item);
      line.classList.remove('wt-price-pending');
    });
    return true;
  }

  function check() {
    var now = Date.now();
    document.querySelectorAll('.cart-item.wt-price-pending').forEach(function (line) {
      if (!seen.has(line)) seen.set(line, now);
      settleLine(line, now - seen.get(line) > MAX_WAIT);
    });

    // Each subtotal waits only for the lines of its own cart: the drawer's
    // subtotal for the drawer's lines, the cart page's for the page's (the
    // cart page also carries a hidden copy of the drawer).
    document.querySelectorAll('.totals.wt-price-pending').forEach(function (t) {
      if (!seen.has(t)) seen.set(t, now);
      if (now - seen.get(t) > MAX_WAIT) {
        if (t.__wtFallback) return;
        t.__wtFallback = true;
        // Only when Easify never re-priced any of its lines is the subtotal
        // worked out here; otherwise Easify's own subtotal stands.
        var scope = t.closest('cart-drawer') || document;
        var easifyDone = !!scope.querySelector('.tpo_cart_item_price');
        readCart().then(function (cart) {
          if (cart && !easifyDone) writeSubtotal(t, cart);
          t.classList.remove('wt-price-pending');
        });
        return;
      }
      var inDrawer = !!t.closest('cart-drawer');
      var waiting = [].some.call(document.querySelectorAll('.cart-item.wt-price-pending'), function (line) {
        return !!line.closest('cart-drawer') === inDrawer;
      });
      if (waiting || t.__wtSettling) return;
      t.__wtSettling = true;
      setTimeout(function () {
        t.__wtSettling = false;
        t.classList.remove('wt-price-pending');
      }, SETTLE);
    });
  }

  var queued = false;
  function queue() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () { queued = false; check(); });
  }

  /* ------------------------------------------------------------------
     Safety net 2 — the charge.
     Easify only charges a line's upgrades at checkout if the line carries
     its tag (_tpo_add_by: easify), which it adds when the customer presses
     Add to Cart. Should a line with priced upgrades ever reach the cart
     without it, the tag is added here — keeping every option and the
     quantity exactly as they are — so the upgrades are always charged.
     Checked on each page with a cart, whenever the drawer opens, and after
     anything is added to the cart.
     ------------------------------------------------------------------ */
  var tagged = {};
  function ensureTagged() {
    readCart().then(function (cart) {
      if (!cart || !cart.items) return;
      cart.items.forEach(function (item) {
        var props = item.properties || {};
        if (props._tpo_add_by || isEasifyHelperLine(item) || !upgradesPerUnit(item)) return;
        if (tagged[item.key]) return;
        tagged[item.key] = true;
        fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: item.quantity, properties: Object.assign({}, props, { _tpo_add_by: 'easify' }) })
        }).then(function () {
          cartPromise = null;
          // The row on screen was drawn untagged, at the base price; show the
          // full price for it straight away.
          readCart().then(function (fresh) {
            document.querySelectorAll('.cart-item').forEach(function (row) {
              var it = itemForRow(fresh, row);
              if (it && it.properties && it.properties._tpo_add_by && !row.querySelector('.tpo_cart_item_price') && upgradesPerUnit(it)) writeLinePrice(row, it);
            });
            document.querySelectorAll('.totals').forEach(function (t) { if (fresh) writeSubtotal(t, fresh); });
          });
        }).catch(function () { tagged[item.key] = false; });
      });
    });
  }

  function watchForTagging() {
    ensureTagged();
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      new MutationObserver(function () {
        if (drawer.classList.contains('active')) ensureTagged();
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
    if ('PerformanceObserver' in window) {
      try {
        new PerformanceObserver(function (list) {
          list.getEntries().forEach(function (entry) {
            if (/[/]cart[/]add/.test(entry.name)) setTimeout(ensureTagged, 1200);
          });
        }).observe({ type: 'resource', buffered: false });
      } catch (e) { /* the page-load and drawer checks still cover it */ }
    }
  }

  function start() {
    check();
    new MutationObserver(queue).observe(document.body, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['class']
    });
    // Keeps the time-out honest even when nothing on the page changes.
    setInterval(function () {
      if (document.querySelector('.wt-price-pending')) check();
    }, 500);
    watchForTagging();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
